import { Direction } from "radix-ui";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { ar } from "./ar";
import { en, type MessageKey } from "./en";

export type Lang = "en" | "ar";
type Vars = Record<string, string | number>;

const dictionaries: Record<Lang, Record<MessageKey, string>> = { en, ar };
const STORAGE_KEY = "s3h.lang";
const TIME_ZONE = "Asia/Damascus";

function initialLang(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "en" || saved === "ar") return saved;
  } catch {
    /* storage unavailable */
  }
  return navigator.language?.toLowerCase().startsWith("ar") ? "ar" : "en";
}

function makeFormatters(lang: Lang) {
  const locale = lang === "ar" ? "ar-SY" : "en-GB";
  const date = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    day: "numeric",
    month: "short",
  });
  const time = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const dateTime = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const longDate = new Intl.DateTimeFormat(locale, {
    timeZone: TIME_ZONE,
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  const number = new Intl.NumberFormat(locale);
  const compact = new Intl.NumberFormat(locale, { notation: "compact", maximumFractionDigits: 1 });
  const relative = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const toDate = (value: string | Date) => (typeof value === "string" ? new Date(value) : value);

  return {
    date: (v: string | Date) => date.format(toDate(v)),
    time: (v: string | Date) => time.format(toDate(v)),
    dateTime: (v: string | Date) => dateTime.format(toDate(v)),
    longDate: (v: string | Date) => longDate.format(toDate(v)),
    number: (v: number) => number.format(v),
    compact: (v: number) => compact.format(v),
    usd: (v: number | string, digits = 2) => {
      const amount = new Intl.NumberFormat(locale, {
        minimumFractionDigits: digits,
        maximumFractionDigits: Math.max(digits, 2),
      }).format(Number(v));
      return lang === "ar" ? `${amount} $` : `$${amount}`;
    },
    relative: (v: string | Date) => {
      const seconds = Math.round((toDate(v).getTime() - Date.now()) / 1000);
      const abs = Math.abs(seconds);
      if (abs < 60) return relative.format(seconds, "second");
      if (abs < 3600) return relative.format(Math.round(seconds / 60), "minute");
      if (abs < 86400) return relative.format(Math.round(seconds / 3600), "hour");
      return relative.format(Math.round(seconds / 86400), "day");
    },
  };
}

export type Formatters = ReturnType<typeof makeFormatters>;

interface I18nValue {
  lang: Lang;
  dir: "ltr" | "rtl";
  setLang: (lang: Lang) => void;
  t: (key: MessageKey, vars?: Vars) => string;
  has: (key: string) => key is MessageKey;
  fmt: Formatters;
}

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const dir = lang === "ar" ? "rtl" : "ltr";

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const dict = dictionaries[lang];
    const t = (key: MessageKey, vars?: Vars) => {
      let text = dict[key] ?? en[key] ?? key;
      if (vars) {
        for (const [name, v] of Object.entries(vars))
          text = text.replaceAll(`{${name}}`, String(v));
      }
      return text;
    };
    const has = (key: string): key is MessageKey => key in en;
    return { lang, dir, setLang, t, has, fmt: makeFormatters(lang) };
  }, [lang, dir, setLang]);

  return (
    <I18nContext.Provider value={value}>
      <Direction.Provider dir={dir}>{children}</Direction.Provider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

export type { MessageKey };
